import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import PropTypes from 'prop-types';

import { Button } from 'ui-components/components/button';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { Divider } from 'ui-components/components/divider';
import { IconButton } from 'ui-components/components/button/icon-button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { BatchAll } from './batch-all';
import { LayoutSelector } from '../custom-layout/layout-selector';
import { SectionVisibilitySelector } from '../custom-layout/section-visibility-selector';
import { useCustomizeLayoutContext } from '../custom-layout/customize-layout-provider';
import { emptyObject } from 'ui-components/default-props/empty';
import ErrorBoundary from '../../../components/error-boundary';
import { InlineNavigationButtons } from '../navigation/inline-navigation-buttons';
import OrderDetailsNavigationContextProvider from '../context/navigation/order-details-navigation-context-provider';
import { OrderDetailsPrintDocumentButton } from '../../../containers/print-document-button-containers/order-details';
import { OrderInfo } from './order-info';
import { useEntitiesSalesOrder } from '../../../hooks/entities/selectors/sales-orders';
import { useSalesOrderIdContext } from '../../context/sales-order-id-context-provider';
import { useContainsDemoOrder } from '../../../demos/hooks/use-contains-demo-order';

import messages from './messages';
import style from './order-details-nav-bar.module.less';

export const OrderDetailsNavBar = ({ onClose, showNavigationButtons }) => {
  const intl = useIntl();
  const { shouldShowCustomizeToggle, isCustomizeMode, onCustomizeToggled, resetToDefaults } = useCustomizeLayoutContext();
  const salesOrderId = useSalesOrderIdContext();
  const { fulfillmentPlanIds, orderNumber, store } = useEntitiesSalesOrder(salesOrderId) || emptyObject;
  const shipmentCount = fulfillmentPlanIds?.length;
  const isDemoOrder = useContainsDemoOrder(fulfillmentPlanIds);
  return (
    <ErrorBoundary errorMessageId='orderDetails'>
      <div className={style['order-details-nav-bar']}>
        <div className={style['order-details-order-info-section']}>
          <OrderInfo orderNumber={orderNumber} storeGuid={store?.storeGuid} />
        </div>
        <div className={style['nav-bar-actions']}>
          {!isDemoOrder && <BatchAll salesOrderId={salesOrderId} shipmentCount={shipmentCount} />}
          <OrderDetailsPrintDocumentButton salesOrderId={salesOrderId} shipmentCount={shipmentCount} />
          {shouldShowCustomizeToggle && (
            <>
              <Divider className={style.divider} orientation='vertical' />
              <div className={style['customize-toggle-container']}>
                {isCustomizeMode && (
                  <>
                    <SectionVisibilitySelector />
                    <LayoutSelector />
                    <Button onClick={resetToDefaults} theme={THEME_CONSTANTS.link}>
                      <FormattedMessage {...messages.restoreDefaults} />
                    </Button>
                  </>
                )}
                <CheckboxSlider
                  checkboxId='od-customize'
                  checked={isCustomizeMode}
                  className={style['customize-toggle']}
                  labelText={intl.formatMessage(messages.customizeLayoutToggle)}
                  onChange={onCustomizeToggled}
                />
              </div>
            </>
          )}
          <Divider className={style.divider} orientation='vertical' />
          <div className={style['navigation-container']}>
            {showNavigationButtons ? (
              <>
                <OrderDetailsNavigationContextProvider>
                  <InlineNavigationButtons />
                </OrderDetailsNavigationContextProvider>
              </>
            ) : null}
            <IconButton ariaLabelMessageDescriptor={messages.closeOrderDetailsButtonAriaLabel} icon={faTimes} onClick={onClose} />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

OrderDetailsNavBar.propTypes = {
  onClose: PropTypes.func.isRequired,
  showNavigationButtons: PropTypes.bool.isRequired,
};
